// TVTool B7a (spec v2.6 §9.2–§9.5; rulings T1a, D20, D21) — pure helpers behind the Help screen's training videos.
// A training video is a knowledge_assets row the admin published through Olaf (agxladmin B6): tags
// ['training', '<group>'] where the group is the English master heading's words (T1a: "Day 1", "Planting day", …),
// media_url / thumbnail_url = the public copy, title_local may be NULL (fall back to title), media_size_bytes +
// media_duration_seconds from the build. The row's description is never shown here (SD-T1176).
// No network, no DOM — everything here is unit-tested (trainingVideos.test.ts).

export const TRAINING_TAG = 'training';

/** The v3 viewing sets in order (D21); a group outside this list sorts last, after these, by name. */
export const GROUP_ORDER = [
  'Day 1', 'Planting day', 'First week', 'When you need help', 'After the first month', 'After harvest',
] as const;
export type KnownGroup = (typeof GROUP_ORDER)[number];

/** The translation key (help.groups.<key>) for each known group; the raw tag is shown for an unknown one. */
export const GROUP_KEYS: Record<KnownGroup, string> = {
  'Day 1': 'day1',
  'Planting day': 'plantingDay',
  'First week': 'firstWeek',
  'When you need help': 'whenYouNeedHelp',
  'After the first month': 'afterFirstMonth',
  'After harvest': 'afterHarvest',
};

export interface TrainingAsset {
  id: string;
  title: string;
  title_local: string | null;
  media_url: string | null;
  thumbnail_url: string | null;
  media_duration_seconds: number | null;
  media_size_bytes: number | null;
  sort_order: number | null;
  tags: string[] | null;
}

export function isTrainingAsset(a: { tags?: string[] | null }): boolean {
  return Array.isArray(a.tags) && a.tags.includes(TRAINING_TAG);
}

/** The group tag — the first tag that is not 'training' — or null. */
export function groupOf(a: { tags?: string[] | null }): string | null {
  const g = (a.tags ?? []).find((t) => t !== TRAINING_TAG && t.trim() !== '');
  return g ? g.trim() : null;
}

function groupRank(name: string | null): number {
  if (name === null) return GROUP_ORDER.length + 1;
  const i = (GROUP_ORDER as readonly string[]).indexOf(name);
  return i >= 0 ? i : GROUP_ORDER.length;
}

export interface TrainingGroup<T extends TrainingAsset = TrainingAsset> {
  name: string | null;          // the raw tag; null = no group tag
  key: string | null;           // help.groups.<key> for a known group; null → show the raw tag (or "Other")
  assets: T[];
}

/** Published training videos grouped under the v3 viewing sets, known groups first in GROUP_ORDER, unknown groups
 *  after them by name, ungrouped last; within a group by sort_order (the video number) then title. */
export function groupTrainingAssets<T extends TrainingAsset>(assets: T[]): TrainingGroup<T>[] {
  const byName = new Map<string | null, T[]>();
  for (const a of assets) {
    if (!isTrainingAsset(a) || !a.media_url) continue;
    const g = groupOf(a);
    byName.set(g, [...(byName.get(g) ?? []), a]);
  }
  const groups: TrainingGroup<T>[] = Array.from(byName.entries()).map(([name, list]) => ({
    name,
    key: name !== null && name in GROUP_KEYS ? GROUP_KEYS[name as KnownGroup] : null,
    assets: [...list].sort((x, y) => (x.sort_order ?? 999) - (y.sort_order ?? 999) || tileTitle(x).localeCompare(tileTitle(y))),
  }));
  return groups.sort((x, y) => groupRank(x.name) - groupRank(y.name) || String(x.name).localeCompare(String(y.name)));
}

/** The label on a tile: the local title, else the English title (title_local may be NULL — B6). */
export function tileTitle(a: { title: string; title_local: string | null }): string {
  const local = (a.title_local ?? '').trim();
  return local || a.title;
}

/** m:ss from seconds; '' when unknown. */
export function formatLength(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** §9.5 kill switch — ONE setting. DEVIATION (declared in the B7a result): the CCS Offline pattern reads a server
 *  constant through a SECURITY DEFINER function; no farmer-readable setting exists today and B7a may not change the
 *  database, so the switch is the build setting VITE_TRAINING_VIDEOS ("off" = videos hidden everywhere, downloads
 *  stop, the Help screen keeps "Ask us"). This is the only place that reads it; a server read slots in here later. */
export function trainingVideosEnabled(env: Record<string, string | undefined> = import.meta.env as Record<string, string | undefined>): boolean {
  const v = (env.VITE_TRAINING_VIDEOS ?? '').trim().toLowerCase();
  return !(v === 'off' || v === 'false' || v === '0');
}

/** Published copies live under this public path (§6.1); it is what the service worker's training cache matches. */
export const TRAINING_PATH = '/storage/v1/object/public/onboarding-videos/training/';
export function isTrainingUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname.endsWith('.supabase.co') && u.pathname.includes(TRAINING_PATH);
  } catch {
    return false;
  }
}

/** The version a tile carries: the public URL changes with every publish ({NN}_v{n}, §6.1), so a newer publish is a
 *  new key — "New" until played, re-downloaded, the old file removed (§9.4). */
export function versionKey(a: { media_url: string | null; id: string }): string {
  return a.media_url ?? a.id;
}

export interface DownloadPlan<T extends TrainingAsset = TrainingAsset> {
  download: T[];     // in order: the current group first, then the viewing-set order
  remove: string[];  // cached training URLs that no published video uses any more (old versions)
  lowSpace: boolean; // true → only the current group is in `download` and the screen says so
  neededBytes: number;
}

const SPACE_MARGIN = 1.2;   // keep a fifth of the needed bytes spare — a phone at the edge of its quota is not filled

/** §9.3.2 / §9.4: what to fetch and what to drop, given what the cache holds and how much space is free. */
export function planDownloads<T extends TrainingAsset>(
  assets: T[], cachedUrls: Set<string>, freeBytes: number | null, currentGroup: string | null,
): DownloadPlan<T> {
  const groups = groupTrainingAssets(assets);
  const ordered = [...groups.filter((g) => g.name === currentGroup), ...groups.filter((g) => g.name !== currentGroup)];
  const wanted = new Set<string>();
  const missing: T[] = [];
  for (const g of ordered) {
    for (const a of g.assets) {
      wanted.add(a.media_url!);
      if (!cachedUrls.has(a.media_url!)) missing.push(a);
    }
  }
  const remove = Array.from(cachedUrls).filter((u) => isTrainingUrl(u) && !wanted.has(u));
  const neededBytes = missing.reduce((n, a) => n + (a.media_size_bytes ?? 0), 0);
  const lowSpace = freeBytes !== null && missing.length > 0 && neededBytes * SPACE_MARGIN > freeBytes;
  const current = new Set((groups.find((g) => g.name === currentGroup)?.assets ?? []).map((a) => a.media_url));
  const download = lowSpace ? missing.filter((a) => current.has(a.media_url)) : missing;
  return { download, remove, lowSpace, neededBytes };
}
